import { resolveMarket } from '@/lib/markets';

export interface MarketingLinkData {
  country: string;
  accountType: string;
  courseId: number;
  /** Session to enroll in. Null/absent for course-level (course-only) enrollment. */
  sessionId: number | null;
}

export interface OnboardingCountry {
  code: string;
  name: string;
  dialCode: string;
}

// Decode Base64URL token client-side
export function decodeToken(token: string): MarketingLinkData | null {
  try {
    // Remove trailing semicolons and whitespace
    const cleanToken = token.replace(/[;\s]+$/, '');
    // Convert Base64URL to standard Base64
    const base64 = cleanToken.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

// Validate token fields: country, accountType and courseId are required;
// sessionId is optional (null/absent means course-level enrollment) but must
// be a number when present.
export function validateToken(
  data: MarketingLinkData | null,
  countries: OnboardingCountry[]
): { valid: boolean; error?: string; canonicalCountry?: string } {
  if (!data) {
    return { valid: false, error: 'invalidToken' };
  }

  // Check required fields
  if (!data.country || typeof data.country !== 'string') {
    return { valid: false, error: 'missingCountry' };
  }

  if (!data.accountType || typeof data.accountType !== 'string') {
    return { valid: false, error: 'missingAccountType' };
  }

  if (!data.courseId || typeof data.courseId !== 'number') {
    return { valid: false, error: 'missingCourseId' };
  }

  // sessionId is optional (course-only enrollment), so a missing value is fine —
  // but a present, wrong-typed value means the link is corrupt.
  if (data.sessionId != null && typeof data.sessionId !== 'number') {
    return { valid: false, error: 'invalidSessionId' };
  }

  // Resolve aliases (NB/NN → NO, SV → SE) to the canonical country code,
  // then verify the backend recognizes it.
  const market = resolveMarket(data.country);
  if (!market || !countries.some((c) => c.code === market.canonical)) {
    return { valid: false, error: 'unsupportedCountry' };
  }

  return { valid: true, canonicalCountry: market.canonical };
}
